import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	BilletProcessPickerCrew,
	BilletProcessPickerProductSize,
	BilletProcessPickerProductType,
	BilletProcessPickerVariableName,
	BilletProcessPickers,
	BilletProcessRow,
	BilletProcessVariableName,
} from "../models/BilletsProcessData";

export interface GetBilletsProcessDataParams {
	start: string;
	end: string;
	variableName?: BilletProcessVariableName | null;
	crewId?: string | null;
	productSizeId?: number | null;
	productTypeId?: number | null;
	signal?: AbortSignal;
}

export const getBilletsProcessData = async ({
	start,
	end,
	variableName = null,
	crewId = null,
	productSizeId = null,
	productTypeId = null,
	signal,
}: GetBilletsProcessDataParams): Promise<BilletProcessRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "variableName", value: variableName },
		{ name: "crewId", value: crewId },
		{ name: "productSizeId", value: productSizeId },
		{ name: "productTypeId", value: productTypeId },
	];

	const spName = "[MILL].[GetBilletsProcessDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching billets process data");
	}
	return get(resp, "data.tables[0].rows", []) as BilletProcessRow[];
};

export interface GetBilletsProcessPickersParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getBilletsProcessPickers = async ({
	start,
	end,
	signal,
}: GetBilletsProcessPickersParams): Promise<BilletProcessPickers> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "onlyActives", value: true },
	];

	const spName = "[MILL].[GetBilletsProcessPickersDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching billets process pickers");
	}

	const variableNames = get(
		resp,
		"data.tables[1].rows",
		[],
	) as BilletProcessPickerVariableName[];
	const crews = get(
		resp,
		"data.tables[2].rows",
		[],
	) as BilletProcessPickerCrew[];
	const productSizes = get(
		resp,
		"data.tables[3].rows",
		[],
	) as BilletProcessPickerProductSize[];
	const productTypes = get(
		resp,
		"data.tables[4].rows",
		[],
	) as BilletProcessPickerProductType[];

	return { variableNames, crews, productSizes, productTypes };
};
